// お知らせ・改善履歴の静的データ（バンドル同梱・バックエンド通信ゼロ）。
//
// 編集ルール（参照: docs/specs を移植した `tender-jumping-swing.md`）：
// - 配列の先頭が最新リリース。新規リリースは先頭に追加する
// - version は一意の文字列（semver 推奨）、date は YYYY-MM-DD
// - entries.audience を省略すると 'all' 扱い
// - entry.fromFeedback: true は「ご要望にお応えしました」バナー用フラグ

export type EntryType = 'feature' | 'improvement' | 'fix' | 'security';

export interface Entry {
  type: EntryType;
  title: string;
  description?: string;
  fromFeedback?: boolean;
  /** 'all'（既定） or 'admin'（管理者のみ可視） */
  audience?: 'all' | 'admin';
}

export interface Release {
  version: string;
  date: string;
  title: string;
  entries: Entry[];
}

export const CHANGELOG: Release[] = [
  {
    version: '0.9.2',
    date: '2026-06-06',
    title: 'フィードバックに運営から返信が届くようになりました',
    entries: [
      {
        type: 'feature',
        title: 'フィードバックに運営が返信し、やり取りできる機能を追加しました',
        description:
          '送信したフィードバックに運営から返信が届き、その場で続けてやり取りできるようになりました。返信が届くと💬ボタンに通知が表示されます。',
      },
    ],
  },
  {
    version: '0.9.1',
    date: '2026-06-05',
    title: 'コメントを編集できるようになりました',
    entries: [
      {
        type: 'feature',
        title: 'コメントを編集できる機能を追加しました',
        fromFeedback: true,
        description:
          '投稿したコメントを後から編集できるようになりました。検証メモを含むすべての投稿で利用できます。',
      },
    ],
  },
  {
    version: '0.9.0',
    date: '2026-06-05',
    title: '「プロジェクト」を「トピック」に名称変更',
    entries: [
      {
        type: 'improvement',
        title: '投稿をまとめる「プロジェクト」を「トピック」に名称変更しました',
        description:
          'URL共有・Q&A・検証メモ・作成アプリ・TIPS を束ねる単位の呼び名を「プロジェクト」から「トピック」に変更しました。達成に向かう作業（プロジェクト）よりも、関連する知識をまとめて参照する場という実際の使われ方に合わせています。サイドバー、投稿フォームの選択欄、各詳細ページの表示がすべて「トピック」になります。これまでに作成した内容や紐付け、ブックマークしたURLはそのまま引き継がれ、操作方法は変わりません。',
      },
    ],
  },
  {
    version: '0.8.0',
    date: '2026-05-14',
    title: 'マイページ内検索と投稿フォーム整理',
    entries: [
      {
        type: 'feature',
        title: 'マイページ内で自分の投稿を検索できるようにしました',
        description:
          'マイページの「すべて」「自分のURL」「自分の質問」「自分の検証メモ」「自分の TIPS」「自分の作成アプリ」タブに検索バーを追加しました。タイトル・概要・コメント・本文などをまたいだキーワード検索ができ、横断検索ページ（/search）と違って自分の非公開投稿も対象に含まれます。スペース区切りで AND 検索、ひらがな/カタカナ・大文字小文字を区別しません。可視性フィルタ（共有中 / 非公開）との併用も可能です。',
      },
      {
        type: 'improvement',
        title: 'URL共有の登録項目から「ステータス」を削除しました',
        description:
          '「未確認 / 確認中 / 検証済み / 採用候補 / 保留」のステータスは、実際には検索や絞り込みに使われておらず、登録時の選択負担だけが残っていたため削除しました。一覧・詳細ページのバッジ表示もなくなります。レビュー進捗管理が必要になった時点で、フィルタ機能とセットで再設計する方針です。既存の投稿データに影響はありません。',
      },
    ],
  },
  {
    version: '0.7.0',
    date: '2026-05-13',
    title: '横断検索と回遊性の改善',
    entries: [
      {
        type: 'feature',
        title: '全コレクション横断検索ページを追加しました',
        description:
          'サイドバーの検索ボックスにキーワードを入力して Enter を押すと、URL共有・Q&A・TIPS・検証メモ・作成アプリをまとめて検索できる「検索結果」ページに遷移します。種別タブと件数バッジで絞り込みでき、未ログイン時もログイン後に検索語を引き継いだ状態で結果を確認できます。',
      },
      {
        type: 'improvement',
        title: 'サイドバーのロゴからホームに戻れるようにしました',
        description:
          '左上のロゴをクリックするとホーム（ダッシュボード）に戻れるようになりました。キーボード操作時のフォーカスリングと、ホバー時のアクセントカラー強調も追加しています。あわせて未実装だった検索ボックスの ⌘K バッジを削除して見た目を整理しました。',
      },
      {
        type: 'improvement',
        title: 'マイページの「自分のURL」に概要行を追加しました',
        description:
          'マイページの URL 一覧に、各リンクの概要を 1 行で表示するようになりました。OG の summary が空でも、自分のコメント／チャンネル名／ドメイン＋種別のいずれかが必ず表示されるので、タイトルだけでは内容が分かりづらかった投稿も一目で見分けられます。',
      },
    ],
  },
  {
    version: '0.6.0',
    date: '2026-05-09',
    title: 'TIPS ページとタグ絞り込み強化',
    entries: [
      {
        type: 'feature',
        title: 'TIPS ページを新設しました',
        description:
          '左メニューに「TIPS」を追加。短いハウツーやベストプラクティス（Markdown 単一本文、最大4000文字）を投稿・検索・コメント・お気に入りできます。Q&A や 検証メモ と使い分けてください。',
      },
      {
        type: 'improvement',
        title: 'URL共有のタグ絞り込みを全 type 対応にしました',
        description:
          'これまで「ツール」タグだけだったタグフィルタを、すべての type（用途・分野など）で利用できるよう拡張。デフォルトは折りたたみ表示で、選択数バッジ付きヘッダから展開できます。',
      },
      {
        type: 'improvement',
        title: 'TIPS 関連の管理機能を追加しました',
        description:
          'マイページ「自分の TIPS」タブ、お気に入り一覧での TIPS 表示、管理者向けモデレーション画面での TIPS 削除導線（admin_logs に delete_tip を記録）を追加しました。',
        audience: 'admin',
      },
    ],
  },
  {
    version: '0.5.0',
    date: '2026-05-01',
    title: '安全性と運用基盤の強化',
    entries: [
      {
        type: 'security',
        title: 'パスワード入力ミス防止を追加しました',
        description:
          'アカウント作成画面に「パスワード（確認）」欄と表示トグル（👁）を追加しました。両欄が一致するまで送信ボタンが無効化され、入力ミスでログイン不能になる事故を防げます。',
      },
      {
        type: 'improvement',
        title: 'タグマスタを本番環境に投入しました',
        description:
          '記事・質問・検証メモなどに付けられる初期タグ（RAG / Dify / Claude / FAQ検索 など 23 種）を本番に投入しました。投稿時にタグ選択がしやすくなります。',
      },
      {
        type: 'improvement',
        title: 'リリース前の自動チェックを強化',
        description:
          'GitHub Actions で型検査・ユニットテスト・Firestore セキュリティルールテストを Pull Request 単位に必須化。Branch protection と組み合わせ、main ブランチへの直接 push を不可にしました。',
        audience: 'admin',
      },
    ],
  },
  {
    version: '0.4.0',
    date: '2026-04-28',
    title: 'お知らせ機能とフィードバック機能を追加',
    entries: [
      {
        type: 'feature',
        title: 'お知らせ機能を追加しました',
        description:
          'アプリの改善履歴・新機能告知を Topbar 直下のバナーと「お知らせ」ページで確認できるようになりました。',
      },
      {
        type: 'feature',
        title: 'フィードバック機能を追加しました',
        description:
          '右下の💬ボタンから、バグ報告・機能要望・その他の意見を直接運営に送れます。',
      },
    ],
  },
];
